import { GeneratorInput, GeneratedSlide } from '../types/generator'
import { parseReference, getPassage } from './bibleApi'
import { findTopicByKeyword, getAllTopics as getTopicsSync } from '../data/topicMappings'
import { getBookById } from '../config/bibleConfig'

/**
 * Extract key themes and meaningful concepts from passage text
 */
function extractThemes(text: string): string[] {
  const commonWords = new Set(['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those', 'a', 'an', 'said', 'says', 'say', 'lord', 'god', 'christ', 'jesus', 'spirit', 'holy', 'heaven', 'earth', 'people', 'man', 'men', 'woman', 'women', 'son', 'father', 'king', 'kingdom'])
  
  // Meaningful biblical/theological terms that indicate themes
  const themeIndicators = new Map<string, string>([
    ['love', 'Love'],
    ['faith', 'Faith'],
    ['hope', 'Hope'],
    ['peace', 'Peace'],
    ['joy', 'Joy'],
    ['grace', 'Grace'],
    ['mercy', 'Mercy'],
    ['righteousness', 'Righteousness'],
    ['justice', 'Justice'],
    ['salvation', 'Salvation'],
    ['redemption', 'Redemption'],
    ['forgiveness', 'Forgiveness'],
    ['repentance', 'Repentance'],
    ['obedience', 'Obedience'],
    ['discipleship', 'Discipleship'],
    ['prayer', 'Prayer'],
    ['worship', 'Worship'],
    ['servant', 'Service'],
    ['humble', 'Humility'],
    ['wisdom', 'Wisdom'],
    ['knowledge', 'Knowledge'],
    ['understanding', 'Understanding'],
    ['truth', 'Truth'],
    ['light', 'Light'],
    ['darkness', 'Darkness'],
    ['sin', 'Sin'],
    ['temptation', 'Temptation'],
    ['trial', 'Trials'],
    ['suffering', 'Suffering'],
    ['perseverance', 'Perseverance'],
    ['endurance', 'Endurance'],
    ['promise', 'Promises'],
    ['covenant', 'Covenant'],
    ['blessing', 'Blessings'],
    ['eternal', 'Eternal Life'],
    ['resurrection', 'Resurrection'],
    ['glory', 'Glory'],
    ['praise', 'Praise'],
    ['thanksgiving', 'Thanksgiving'],
  ])
  
  const words = text.toLowerCase().match(/\b[a-z]+\b/g) || []
  const wordCount = new Map<string, number>()
  const foundThemes = new Set<string>()
  
  // First, look for known theme indicators
  words.forEach(word => {
    if (themeIndicators.has(word)) {
      foundThemes.add(themeIndicators.get(word)!)
    }
  })
  
  // Then count other meaningful words (longer words that aren't common)
  words.forEach(word => {
    if (!commonWords.has(word) && word.length > 4 && !themeIndicators.has(word)) {
      wordCount.set(word, (wordCount.get(word) || 0) + 1)
    }
  })
  
  // Combine found themes with most frequent meaningful words
  const themes = Array.from(foundThemes)
  const topWords = Array.from(wordCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5 - themes.length)
    .map(([word]) => word.charAt(0).toUpperCase() + word.slice(1))
  
  const allThemes = [...themes, ...topWords]
  
  // Return up to 3 most relevant themes
  return allThemes.slice(0, 3)
}

/**
 * Split text into sentences for better slide content
 */
function splitIntoParagraphs(text: string, maxLength: number = 300): string[] {
  // Split by verse numbers first, then by sentences
  const sentences = text.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0)
  const paragraphs: string[] = []
  let current = ''
  
  for (const sentence of sentences) {
    if ((current + ' ' + sentence).length > maxLength && current) {
      paragraphs.push(current.trim())
      current = sentence
    } else {
      current = current ? current + ' ' + sentence : sentence
    }
  }
  
  if (current) {
    paragraphs.push(current.trim())
  }
  
  return paragraphs.length > 0 ? paragraphs : [text]
}

/**
 * Parse verses from passage text more accurately
 */
function parseVersesFromText(text: string, _bookName: string, _chapter: number, startVerse?: number, endVerse?: number): Array<{ verse: number; text: string }> {
  const verses: Array<{ verse: number; text: string }> = []
  
  // Pattern to match verse numbers at the start of lines
  const versePattern = /^(\d+)\s+(.+?)(?=^\d+\s+|$)/gm
  let match
  
  while ((match = versePattern.exec(text)) !== null) {
    const verseNum = parseInt(match[1], 10)
    const verseText = match[2].trim()
    
    if (!startVerse || (verseNum >= startVerse && (!endVerse || verseNum <= endVerse))) {
      verses.push({ verse: verseNum, text: verseText })
    }
  }
  
  // If no verse numbers found, try to split by paragraphs or sentences
  if (verses.length === 0) {
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0)
    paragraphs.forEach((para, idx) => {
      verses.push({ verse: (startVerse || 1) + idx, text: para.trim() })
    })
  }
  
  return verses
}

/**
 * Generate slides from a Bible reference
 */
async function generateFromReference(
  reference: string,
  options: { includeExplanations?: boolean; includeApplications?: boolean; maxSlides?: number }
): Promise<GeneratedSlide[]> {
  const parsed = parseReference(reference)
  if (!parsed) {
    throw new Error(`Invalid Bible reference: ${reference}`)
  }

  const book = getBookById(parsed.bookId)
  if (!book) {
    throw new Error(`Book not found: ${parsed.bookId}`)
  }

  const slides: GeneratedSlide[] = []

  // Title slide
  const refDisplay = `${book.name} ${parsed.chapter}${parsed.startVerse ? `:${parsed.startVerse}` : ''}${parsed.endVerse && parsed.endVerse !== parsed.startVerse ? `-${parsed.endVerse}` : ''}`
  slides.push({
    type: 'title',
    title: refDisplay,
    subtitle: `Bible Study Presentation`,
    content: `A study of ${book.name}${parsed.startVerse ? ` Chapter ${parsed.chapter}` : ` Chapter ${parsed.chapter}`}`,
    generated: true,
    sourceReference: reference,
  })

  // Get the passage text
  const passageText = await getPassage(reference)
  if (!passageText) {
    throw new Error(`Could not fetch passage: ${reference}`)
  }

  // Parse verses from the text
  const verses = parseVersesFromText(passageText, book.name, parsed.chapter, parsed.startVerse, parsed.endVerse)
  
  // Determine how many content slides we can create
  const maxContentSlides = (options.maxSlides || 10) - (options.includeExplanations ? 1 : 0) - (options.includeApplications ? 1 : 0) - 2 // title and conclusion
  
  if (verses.length === 0) {
    // If we can't parse verses, split the text into paragraphs
    const paragraphs = splitIntoParagraphs(passageText.replace(/\d+\s+/g, '').trim())
    const paragraphsPerSlide = Math.max(1, Math.ceil(paragraphs.length / maxContentSlides))
    
    for (let i = 0; i < paragraphs.length; i += paragraphsPerSlide) {
      const slideParagraphs = paragraphs.slice(i, i + paragraphsPerSlide)
      slides.push({
        type: 'verse',
        title: `${book.name} ${parsed.chapter}${parsed.startVerse ? `:${parsed.startVerse}` : ''}${parsed.endVerse ? `-${parsed.endVerse}` : ''} (Part ${Math.floor(i / paragraphsPerSlide) + 1})`,
        verse: slideParagraphs.join('\n\n'),
        verseReference: `${refDisplay} (ESV)`,
        generated: true,
        sourceReference: reference,
      })
    }
  } else if (verses.length === 1 || (parsed.startVerse && !parsed.endVerse)) {
    // Single verse
    slides.push({
      type: 'verse',
      title: `${book.name} ${parsed.chapter}:${verses[0].verse}`,
      verse: verses[0].text,
      verseReference: `${book.name} ${parsed.chapter}:${verses[0].verse} (ESV)`,
      generated: true,
      sourceReference: reference,
    })
  } else {
    // Multiple verses - split intelligently
    const versesPerSlide = Math.max(1, Math.ceil(verses.length / maxContentSlides))
    
    for (let i = 0; i < verses.length; i += versesPerSlide) {
      const slideVerses = verses.slice(i, i + versesPerSlide)
      const firstVerse = slideVerses[0]
      const lastVerse = slideVerses[slideVerses.length - 1]

      const verseText = slideVerses.map(v => v.text).join(' ')
      const verseRef = slideVerses.length === 1
        ? `${firstVerse.verse}`
        : `${firstVerse.verse}-${lastVerse.verse}`

      slides.push({
        type: 'verse',
        title: `${book.name} ${parsed.chapter}:${verseRef}`,
        verse: verseText,
        verseReference: `${book.name} ${parsed.chapter}:${verseRef} (ESV)`,
        generated: true,
        sourceReference: reference,
      })
    }
  }

  // Extract themes for more contextual slides
  const cleanText = passageText.replace(/\d+\s+/g, '').trim()
  // Themes extracted but not used in slides (removed per user request)
  void extractThemes(cleanText)

  // Add explanation slide if requested
  if (options.includeExplanations) {
    const contextPoints: string[] = [
      `This passage from ${book.name} teaches us about God's character and His will`,
      'Consider the historical and cultural context',
      'Look for commands, promises, warnings, or instructions',
      'Notice how this passage relates to the broader message of Scripture',
      'Reflect on what God is revealing about Himself and His plan',
    ]
    
    slides.push({
      type: 'explanation',
      title: 'Understanding the Passage',
      content: `Let's explore what ${book.name} ${parsed.chapter}${parsed.startVerse ? `:${parsed.startVerse}` : ''} teaches us.`,
      points: contextPoints,
      generated: true,
      sourceReference: reference,
    })
  }

  // Add application slide if requested
  if (options.includeApplications) {
    const applicationPoints = [
      'What does this passage reveal about God\'s character?',
      'How does this apply to my relationship with God?',
      'What does this teach me about how I should live?',
      'Are there specific actions or changes this calls for?',
      'How can I share or apply this truth in my community?',
    ]
    
    slides.push({
      type: 'explanation',
      title: 'Application',
      content: `How can we apply ${book.name} ${parsed.chapter}${parsed.startVerse ? `:${parsed.startVerse}` : ''} to our lives today?`,
      points: applicationPoints,
      generated: true,
      sourceReference: reference,
    })
  }

  // Conclusion slide
  slides.push({
    type: 'conclusion',
    title: 'Conclusion',
    content: `Thank you for studying ${refDisplay} with us.`,
    points: [
      'May God bless you as you meditate on His word',
      'Continue to seek Him daily through Scripture',
      'Share what you\'ve learned with others',
    ],
    generated: true,
    sourceReference: reference,
  })

  return slides
}

/**
 * Generate slides from a topic
 */
async function generateFromTopic(
  topic: string,
  options: { includeExplanations?: boolean; includeApplications?: boolean; maxSlides?: number }
): Promise<GeneratedSlide[]> {
  const topicMapping = findTopicByKeyword(topic)
  if (!topicMapping) {
    const availableTopics = getTopicsSync()
    throw new Error(`Topic not found: ${topic}. Available topics: ${availableTopics.join(', ')}`)
  }

  const slides: GeneratedSlide[] = []
  const topicTitle = topicMapping.topic.charAt(0).toUpperCase() + topicMapping.topic.slice(1)

  // Title slide
  slides.push({
    type: 'title',
    title: topicTitle,
    subtitle: 'Bible Study Presentation',
    content: topicMapping.description || `A study on ${topicMapping.topic}`,
    points: [
      `Exploring what Scripture teaches about ${topicTitle}`,
      `Multiple passages from different books`,
      `Practical application for today`,
    ],
    generated: true,
    sourceTopic: topic,
  })

  // Calculate how many verse slides we can create
  const maxContentSlides = (options.maxSlides || 10) - (options.includeExplanations ? 1 : 0) - (options.includeApplications ? 1 : 0) - 2 // title and conclusion
  const referencesToUse = topicMapping.references.slice(0, Math.min(maxContentSlides, topicMapping.references.length))

  // Collect all passage texts to extract themes
  const allPassageTexts: string[] = []

  // Generate slides for each reference
  for (let idx = 0; idx < referencesToUse.length; idx++) {
    const ref = referencesToUse[idx]
    try {
      const passageText = await getPassage(ref)
      if (passageText) {
        allPassageTexts.push(passageText)
        const parsed = parseReference(ref)
        if (parsed) {
          const cleanText = passageText.replace(/\d+\s+/g, '').trim()
          
          // For longer passages, split into paragraphs
          const paragraphs = splitIntoParagraphs(cleanText, 250)
          
          if (paragraphs.length === 1) {
            // Single slide for this reference
            slides.push({
              type: 'verse',
              title: ref,
              verse: cleanText,
              verseReference: `${ref} (ESV)`,
              generated: true,
              sourceTopic: topic,
            })
          } else {
            // Multiple slides for longer passages
            paragraphs.forEach((para, paraIdx) => {
              slides.push({
                type: 'verse',
                title: paraIdx === 0 ? ref : `${ref} (continued)`,
                verse: para,
                verseReference: `${ref} (ESV)`,
                generated: true,
                sourceTopic: topic,
              })
            })
          }
        }
      }
    } catch (error) {
      console.warn(`Failed to fetch passage ${ref}:`, error)
    }
  }

  // Extract themes from all passages
  const allText = allPassageTexts.join(' ').replace(/\d+\s+/g, '').trim()
  // Themes extracted but not used in slides (removed per user request)
  void extractThemes(allText)

  // Add explanation slide if requested
  if (options.includeExplanations) {
    const insightPoints: string[] = [
      `The Bible has much to say about ${topicTitle}`,
      'These passages reveal God\'s perspective',
      'Notice how different authors address this topic',
      'Look for patterns and repeated concepts',
      'Consider how this relates to the gospel message',
    ]
    
    slides.push({
      type: 'explanation',
      title: 'Key Insights',
      content: `What does the Bible teach us about ${topicTitle}?`,
      points: insightPoints,
      generated: true,
      sourceTopic: topic,
    })
  }

  // Add application slide if requested
  if (options.includeApplications) {
    const applicationPoints = [
      `How does what we've learned about ${topicTitle} change my perspective?`,
      'What specific actions should I take?',
      'How can I share these truths with others?',
      'What prayer can I offer in response?',
      'What will I do differently this week?',
    ]
    
    slides.push({
      type: 'explanation',
      title: 'Application',
      content: `How can we apply what we've learned about ${topicTitle}?`,
      points: applicationPoints,
      generated: true,
      sourceTopic: topic,
    })
  }

  // Conclusion slide
  slides.push({
    type: 'conclusion',
    title: 'Conclusion',
    content: `Thank you for studying ${topicTitle} with us.`,
    points: [
      'Continue to meditate on these truths',
      'Ask God to help you apply them to your life',
      'Share what you\'ve learned with others',
    ],
    generated: true,
    sourceTopic: topic,
  })

  return slides
}

/**
 * Main function to generate slides
 */
export async function generateSlides(input: GeneratorInput): Promise<GeneratedSlide[]> {
  try {
    if (input.mode === 'reference') {
      if (!input.reference) {
        throw new Error('Reference is required for reference mode')
      }
      return await generateFromReference(input.reference, {
        includeExplanations: input.includeExplanations,
        includeApplications: input.includeApplications,
        maxSlides: input.maxSlides,
      })
    } else if (input.mode === 'topic') {
      if (!input.topic) {
        throw new Error('Topic is required for topic mode')
      }
      return await generateFromTopic(input.topic, {
        includeExplanations: input.includeExplanations,
        includeApplications: input.includeApplications,
        maxSlides: input.maxSlides,
      })
    } else {
      throw new Error(`Invalid mode: ${input.mode}`)
    }
  } catch (error) {
    console.error('Error generating slides:', error)
    throw error
  }
}

/**
 * Get all available topics
 */
export async function getAllTopics(): Promise<string[]> {
  const module = await import('../data/topicMappings')
  return module.getAllTopics()
}

